import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faPlus, faTrash, faCheck, faQuestion, faTimes } from '@fortawesome/free-solid-svg-icons';
import { getCommunity } from '../../../../services/communityService';
import { useLanguage } from '../../../../contexts/LanguageContext';

function AttendeesTab({ 
    availableUsers, 
    existingAttendees, 
    selectedUsers, 
    setSelectedUsers, 
    userSearchTerm, 
    setUserSearchTerm, 
    onAddAttendees, 
    onRemoveAttendee,
    communityId = null,
    isMobile = false
}) {
    const { t } = useLanguage();
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [communityName, setCommunityName] = useState('');
    const [isLoadingCommunity, setIsLoadingCommunity] = useState(false);
    
    // Debug logging
    useEffect(() => {
    }, [communityId, availableUsers]);

    // Fetch proper community name
    useEffect(() => {
        const fetchCommunityName = async () => {
            // Default for no community
            if (!communityId) {
                setCommunityName(t('events.modals.attendees.noCommunity') || 'No community');
                return;
            }
            
            setIsLoadingCommunity(true);
            try {
                // Fetch the community details to get the proper name
                const communityData = await getCommunity(communityId);
                if (communityData && communityData.name) {
                    setCommunityName(communityData.name);
                } else {
                    // Fallback if we couldn't get the name
                    setCommunityName(`${t('community.community')} ${communityId}`);
                }
            } catch (error) {
                // Fallback on error
                setCommunityName(`${t('community.community')} ${communityId}`);
            } finally {
                setIsLoadingCommunity(false);
            }
        };
        
        fetchCommunityName();
    }, [communityId]);

    // Filter available users based on search term, existing attendees, and community
    useEffect(() => {
        let result = [...availableUsers];
        
        // CRITICAL: First filter by community ID to avoid "Cannot add users from different communities" error
        if (communityId) {
            // Convert IDs to numbers for safe comparison
            const eventCommunityId = Number(communityId);
            result = result.filter(user => {
                // Convert user's community ID to number for comparison
                const userCommunityId = user.community_id ? Number(user.community_id) : null;
                // Only show users from this community or without a community
                return userCommunityId === eventCommunityId || userCommunityId === null;
            });
        }
        
        // Then filter out users that are already attendees
        if (existingAttendees && existingAttendees.length > 0) {
            const existingUserIds = existingAttendees.map(attendee => attendee.user_id);
            result = result.filter(user => !existingUserIds.includes(user.id));
        }
        
        // Then filter by search term if provided
        if (userSearchTerm && userSearchTerm.trim() !== '') {
            const term = userSearchTerm.toLowerCase().trim();
            result = result.filter(user =>
                user.display_name?.toLowerCase().includes(term) ||
                user.email?.toLowerCase().includes(term)
            );
        }
        
        setFilteredUsers(result);
    }, [availableUsers, userSearchTerm, existingAttendees, communityId]);

    // Handle user selection for adding to event
    const handleUserSelect = (userId) => {
        if (selectedUsers.includes(userId)) {
            setSelectedUsers(selectedUsers.filter(id => id !== userId));
        } else {
            setSelectedUsers([...selectedUsers, userId]);
        }
    };

    // Handle adding selected users as attendees
    const handleAddAttendees = async () => {
        if (selectedUsers.length === 0) return;
        
        setIsSaving(true);
        try {
            await onAddAttendees(selectedUsers);
            setSelectedUsers([]);
        } catch (error) {
        } finally {
            setIsSaving(false);
        }
    };

    // Get status icon based on attendance status
    const getStatusIcon = (status) => {
        switch (status) {
            case 'confirmed':
                return <FontAwesomeIcon icon={faCheck} className="text-success" />;
            case 'declined':
                return <FontAwesomeIcon icon={faTimes} className="text-danger" />;
            default:
                return <FontAwesomeIcon icon={faQuestion} className="text-secondary" />;
        }
    };

    return (
        <div className="attendees-tab">
            {/* Community Context Information */}
            {communityId ? (
                <div className="alert alert-info mb-3 py-2">
                    <small>
                        {isLoadingCommunity ? (
                            <span>{t('events.modals.attendees.loadingCommunity')}</span>
                        ) : (
                            <span>{t('events.modals.attendees.communityUsers', { name: communityName })}</span>
                        )}
                    </small>
                </div>
            ) : (
                <div className="alert alert-warning mb-3 py-2">
                    <small>
                        {t('events.modals.attendees.noCommunityWarning')}
                    </small>
                </div>
            )}

            {/* Available Users Section */}
            <div className="mb-4">
                <h6 className="mb-2">{t('events.modals.attendees.addMembers')}</h6>

                {/* Search users */}
                <div className="input-group mb-3">
                    <span className="input-group-text">
                        <FontAwesomeIcon icon={faSearch} />
                    </span>
                    <input
                        type="text"
                        className="form-control"
                        placeholder={t('events.modals.attendees.searchUsers')}
                        value={userSearchTerm}
                        onChange={(e) => setUserSearchTerm(e.target.value)}
                    />
                </div>
                
                {/* User selection list */}
                <div className="user-selection">
                    <div 
                        className="list-group available-users" 
                        style={{ maxHeight: '200px', overflowY: 'auto' }}
                    >
                        {filteredUsers.length === 0 ? (
                            <div className="list-group-item text-center text-muted">
                                {availableUsers.length === 0 ?
                                    t('events.modals.attendees.noAvailableUsers') :
                                    t('events.modals.attendees.noResults')}
                            </div>
                        ) : (
                            filteredUsers.map(availableUser => (
                                <div 
                                    key={availableUser.id} 
                                    className="list-group-item list-group-item-action p-2"
                                >
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`user-${availableUser.id}`}
                                            checked={selectedUsers.includes(availableUser.id)}
                                            onChange={() => handleUserSelect(availableUser.id)}
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor={`user-${availableUser.id}`}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {availableUser.display_name || availableUser.email?.split('@')[0]}
                                        </label>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                    
                    {/* Add selected users button */}
                    <div className="mt-2 text-end">
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={handleAddAttendees}
                            disabled={selectedUsers.length === 0 || isSaving}
                        >
                            {isSaving ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                    {t('events.modals.attendees.adding')}
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                                    {t('events.modals.attendees.addSelected')} ({selectedUsers.length})
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Existing Attendees Section */}
            <div>
                <h6 className="mb-2">{t('events.modals.attendees.currentTeam')} ({existingAttendees.length})</h6>

                <div
                    className="list-group existing-attendees"
                    style={{ maxHeight: '200px', overflowY: 'auto' }}
                >
                    {existingAttendees.length === 0 ? (
                        <div className="list-group-item text-center text-muted">
                            {t('events.modals.attendees.noMembers')}
                        </div>
                    ) : (
                        existingAttendees.map(attendee => (
                            <div 
                                key={attendee.user_id} 
                                className="list-group-item p-2"
                            >
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                        <div className="me-2">
                                            {getStatusIcon(attendee.status)}
                                        </div>
                                        <div>
                                            {attendee.display_name || attendee.email?.split('@')[0]}
                                        </div>
                                    </div>
                                    <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => onRemoveAttendee(attendee.user_id)}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}

export default AttendeesTab;